import React from 'react';
import { Link } from 'react-router-dom';

const RAIL_NODES = [
  { top: <>Intake &amp;<br />Assessment</>, bot: 'Day One' },
  { top: <>Vocational<br />Education</>, bot: 'Modules Begin' },
  { top: <>Credential<br />Pathway</>, bot: 'Hours Tracked' },
  { top: <>Badge<br />Earned</>, bot: 'Verified · Portable' },
  { top: <>Employer<br />Matched</>, bot: 'Before Release' },
  { top: <>Release<br />Day</>, bot: 'Reentry Plan Active' },
  { top: <>Day One<br />of Work</>, bot: 'Outcome Recorded' },
];

const J_CARDS = [
  { ic: '📋', t: 'Intake & Pathway', p: 'Personalized module recommendations based on education, interests, and facility context from day one.' },
  { ic: '📚', t: 'Vocational Modules', p: '11 workforce-aligned courses covering trades, technology, and healthcare mapped to Texas Targeted Occupations.' },
  { ic: '⏱️', t: 'Clock Hour Tracking', p: 'Real-time hours toward CompTIA, EPA 608, NCCER, NHA — credentials employers recognize and Pell requires.' },
  { ic: '🏅', t: 'Portable Credentials', p: "Digital badges surviving release — accessible post-custody through a learner's portable credential wallet." },
  { ic: '💼', t: 'Employer Pipeline', p: '38 second-chance employer partners matched to learners by credential alignment — before the gate opens.' },
  { ic: '🗺️', t: 'Reentry Planning', p: 'Pre-release checklists, housing referrals, and 30/60/90-day post-release milestones built into the platform.' },
  { ic: '📊', t: 'Evidence Engine', p: 'One-click WIOA, DOL, and Workforce Pell compliance reports — the outcome data grantors require, automated.' },
];

export const Journey = () => (
  <section className="journey" data-label="The Journey" data-testid="journey-section">
    <div className="j-wrap">
      <div className="j-head reveal">
        <div className="eyebrow center">The AssistivOS Journey</div>
        <h2 className="j-h">From the First Day of Intake to the First Day of Work — For the First Time, Connected.</h2>
        <p className="j-p">Every person who moves through AssistivOS follows a documented, credential-verified, employer-connected pathway. The journey that was always possible is finally a system.</p>
      </div>
      <div className="rail-outer" data-testid="journey-rail">
        <div className="rail-track"><div className="rail-fill" /></div>
        <div className="rail-nodes">
          {RAIL_NODES.map((n, i) => (
            <div className="r-n" key={i}>
              <div className="r-d" />
              <div className="r-n-top">{n.top}</div>
              <div className="r-n-bot">{n.bot}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="j-cards" data-drift="0.05">
        {J_CARDS.map((c) => (
          <div className="j-card" key={c.t}>
            <div className="j-card-ic" aria-hidden="true">{c.ic}</div>
            <div className="j-card-t">{c.t}</div>
            <p className="j-card-p">{c.p}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Shot = ({ src, alt, h = 'h-md' }) => (
  <div className={`b-img ${h}`}>
    <div className="shot-bar" aria-hidden="true">
      <span className="sb-d r" /><span className="sb-d y" /><span className="sb-d g" />
      <span className="sb-t">Live Prototype</span>
    </div>
    <img src={src} alt={alt} loading="lazy" />
  </div>
);

export const Bento = () => (
  <section className="bento-sec" id="platform" data-label="The Platform" data-testid="bento-section">
    <div className="bento-top reveal">
      <div className="eyebrow center" style={{ marginBottom: 14 }}>The Platform</div>
      <h2 className="sec-h">Not a Feature. Not an App.<br />An Operating System for Every Stakeholder.</h2>
      <p className="sec-p">Seven purpose-built workflows. One shared data layer. Every outcome visible, verifiable, and fundable. Screens below are the live prototype — not mockups.</p>
    </div>
    <div className="bento">
      <div className="bx-7 reveal tilt" data-drift="0.03" data-testid="bento-admin">
        <span className="bt">Platform Admin · Live Screen</span>
        <div className="bh">Real-Time Analytics &amp; Compliance</div>
        <p className="bp">Platform-wide enrollment, completion, credential attainment, and employer placement — with one-click WIOA, DOL, and Workforce Pell export.</p>
        <Shot src="/screenshots/screenshot-admin.png" alt="AssistivOS admin analytics dashboard" h="h-lg" />
      </div>
      <div className="bx-5 reveal tilt" data-drift="-0.03" style={{ transitionDelay: '.1s' }} data-testid="bento-credential">
        <span className="bt">Credential Tracking</span>
        <div className="bh">EPA 608 Certification</div>
        <p className="bp">Real-time clock hours. Industry-recognized credentials. Portable beyond custody.</p>
        <div className="cred-ring">
          <svg width="110" height="110" viewBox="0 0 90 90">
            <circle cx="45" cy="45" r="38" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
            <circle cx="45" cy="45" r="38" fill="none" stroke="#F97316" strokeWidth="8" strokeLinecap="round" strokeDasharray="239" strokeDashoffset="76" style={{ filter: 'drop-shadow(0 0 6px rgba(249,115,22,.5))' }} />
          </svg>
          <div className="cred-ring-label"><div className="crl-n">68%</div><div className="crl-l">Complete</div></div>
        </div>
        <div style={{ textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: 'var(--t3)', marginBottom: 18 }}>34 of 50 hours toward EPA 608</div>
        <div className="ring-stats">
          <div className="ring-stat"><b>247</b><span>Active learners</span></div>
          <div className="ring-stat"><b>89</b><span>Credentials earned</span></div>
        </div>
      </div>
      <div className="bx-6 reveal tilt" data-drift="0.03" style={{ transitionDelay: '.15s' }} data-testid="bento-employer">
        <span className="bt">Employer Partner · Live Screen</span>
        <div className="bh">Matched Candidate Pipeline</div>
        <p className="bp">Credential-verified candidates matched before release. WOTC and Federal Bonding included.</p>
        <Shot src="/screenshots/screenshot-employer.png" alt="Employer partner dashboard with candidate pipeline" h="h-md" />
      </div>
      <div className="bx-6 reveal tilt" data-drift="-0.03" style={{ transitionDelay: '.2s' }} data-testid="bento-learner">
        <span className="bt">Learner Dashboard · Live Screen</span>
        <div className="bh">Gamified Learning on JPay Tablets — Offline-First</div>
        <p className="bp">XP systems, streaks, credential tracking, and employer matching — delivered offline on institutional tablets including JPay. The full learner journey in one interface.</p>
        <Shot src="/screenshots/screenshot-youth-wide.png" alt="Gamified learner home with streak and learning path" h="h-lg" />
      </div>
      <div className="bx-4 reveal tilt" data-drift="0.03" style={{ transitionDelay: '.25s' }} data-testid="bento-casemanager">
        <span className="bt">Case Manager · Live Screen</span>
        <div className="bh">Reentry Command Center</div>
        <p className="bp">Caseloads, release countdowns, at-risk flags, and reentry checklists in one view.</p>
        <Shot src="/screenshots/screenshot-casemanager.png" alt="Case manager dashboard" h="h-sm" />
      </div>
      <div className="bx-4 reveal tilt" data-drift="-0.03" style={{ transitionDelay: '.3s' }} data-testid="bento-instructor">
        <span className="bt">Instructor · Live Screen</span>
        <div className="bh">Roster &amp; Assessments</div>
        <p className="bp">Per-learner progress bars, knowledge-check scores, and at-risk flagging tools.</p>
        <Shot src="/screenshots/screenshot-instructor.png" alt="Instructor roster view" h="h-sm" />
      </div>
      <div className="bx-4 reveal tilt" data-drift="0.03" style={{ transitionDelay: '.35s' }} data-testid="bento-provider">
        <span className="bt">Training Provider · Live Screen</span>
        <div className="bh">Pell-Compliant Publishing</div>
        <p className="bp">Course publishing, facility approvals, and Pell revenue tracking for college partners.</p>
        <Shot src="/screenshots/screenshot-provider.png" alt="Training provider dashboard" h="h-sm" />
      </div>
      <div className="bx-4 reveal" data-drift="-0.03" style={{ transitionDelay: '.4s' }} data-testid="bento-badges">
        <span className="bt">Badge System</span>
        <div className="bh">Earned Credentials</div>
        <p className="bp">Portable digital badges for every milestone — verified and accessible post-release.</p>
        <div className="badge-display">
          <div className="bd-badge earn">🎓</div><div className="bd-badge earn">⚡</div><div className="bd-badge earn">🔧</div><div className="bd-badge earn">🏅</div>
          <div className="bd-badge lock">🛡️</div><div className="bd-badge lock">💡</div><div className="bd-badge lock">🏭</div>
        </div>
      </div>
      <div className="bx-4 reveal" data-drift="0.03" style={{ transitionDelay: '.45s' }} data-testid="bento-grants">
        <span className="bt">Grant Pathways</span>
        <div className="bh">Federal Funding</div>
        <p className="bp">HDG University-led applications across every major correctional education funding stream.</p>
        <div className="grant-list">
          <div className="gl-item"><div className="gl-name">BJA Second Chance Act</div><div className="gl-amt">≤$900K</div></div>
          <div className="gl-item"><div className="gl-name">DOL Pathway Home</div><div className="gl-amt">≤$500K</div></div>
          <div className="gl-item"><div className="gl-name">Perkins V via TEA</div><div className="gl-amt">Eligible</div></div>
          <div className="gl-item"><div className="gl-name">WIOA Title I &amp; II</div><div className="gl-amt">Eligible</div></div>
        </div>
      </div>
      <div className="bx-4 reveal" data-drift="-0.03" style={{ transitionDelay: '.5s' }} data-testid="bento-revenue">
        <span className="bt">Revenue Model</span>
        <div className="bh">Six Streams</div>
        <p className="bp">DOC licensing · Workforce Pell · Marketplace fees · Employer subscriptions · Compliance tools · Ethical sponsorship.</p>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 32, fontWeight: 500, color: 'var(--or)' }}>$575K</div>
          <div style={{ fontFamily: "'Lato',sans-serif", fontSize: 12, color: 'var(--t3)', marginTop: 4 }}>ARR at operational sustainability milestone<br />10 facilities · 3 colleges · 15 employers</div>
        </div>
      </div>
    </div>
  </section>
);

const TAVUS_DEPLOYMENT_ID = '701a88ca-5198-4358-b955-6681e791ed1b';

const useTavusScript = (active) => {
  const [status, setStatus] = React.useState('idle');
  React.useEffect(() => {
    if (!active) return undefined;
    if (window.customElements?.get('tavus-embed')) { setStatus('ready'); return undefined; }
    setStatus('loading');
    const onLoad = () => setStatus('ready');
    const onError = () => setStatus('failed');
    let script = document.querySelector('script[data-tavus-embed]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://unpkg.com/@tavus/embed@latest';
      script.async = true;
      script.dataset.tavusEmbed = '1';
      document.head.appendChild(script);
    }
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    return () => { script.removeEventListener('load', onLoad); script.removeEventListener('error', onError); };
  }, [active]);
  return status;
};

export const TutorTease = () => {
  const [open, setOpen] = React.useState(false);
  const status = useTavusScript(open);
  return (
    <section className="tutor-tease" data-label="Live Counselor" data-testid="tutor-tease-section">
      <div className="tt-wrap">
        <div className="reveal-l">
          <div className="eyebrow">Live AI Counselor</div>
          <h2 className="sec-h" style={{ color: 'var(--wh)', marginBottom: 16 }}>Every Learner Gets a Counselor Who Never Clocks Out.</h2>
          <p className="sec-p" style={{ marginBottom: 22 }}>
            Inside AssistivOS, learners talk face-to-face with a live AI counselor that already knows
            their pathway, their course, and their progress — explaining concepts, quizzing gently,
            and keeping momentum toward the credential. No appointment. No waitlist. No stigma.
          </p>
          <ul className="tt-points">
            <li>Knows the learner's exact course and lesson the moment it answers</li>
            <li>Voice-first — built for learners who'd rather talk than type</li>
            <li>Available on facility tablets, day one after intake</li>
          </ul>
          <button type="button" className="btn-or magnetic" data-track="tutor-tease" data-testid="tutor-tease-cta" onClick={() => setOpen(true)}>
            Talk to the Live Counselor →
          </button>
        </div>
        <div className="reveal-r">
          <button type="button" className="tt-card" data-track="tutor-tease-card" data-testid="tutor-tease-card" onClick={() => setOpen(true)} aria-label="Open the live AI counselor">
            <img src="/screenshots/tutor-avatar.png" alt="" aria-hidden="true" className="tt-card-bg" />
            <div className="tt-card-scrim" />
            <div className="tt-live"><span className="tt-live-dot" />LIVE · AI COUNSELOR</div>
            <div className="tt-play"><span>▶</span></div>
            <div className="tt-card-cap">"Hey Jonathan! Ready to keep going on Banking &amp; Budgeting Basics?"</div>
          </button>
        </div>
      </div>
      {open && (
        <div className="mkt-modal-overlay" data-testid="tutor-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="mkt-modal tt-modal" role="dialog" aria-modal="true" data-testid="tutor-modal">
            <button type="button" className="mkt-modal-x" aria-label="Close" data-testid="tutor-modal-close" onClick={() => setOpen(false)}>✕</button>
            <span className="mkt-modal-tag">Live AI Counselor</span>
            <h3 className="mkt-modal-h" style={{ fontSize: 19 }}>Say hello — it talks back.</h3>
            <div className="tt-embed-box">
              {status !== 'ready' && (
                <div className="tt-embed-msg" data-testid="tutor-modal-loading">
                  {status === 'failed' ? "The live counselor couldn't load — check your connection and try again." : 'Connecting to the live counselor…'}
                </div>
              )}
              {status === 'ready' && (
                <tavus-embed
                  deployment-id={TAVUS_DEPLOYMENT_ID}
                  conversational-context="You are the AssistivOS live AI counselor being previewed by an investor or partner on the marketing site. Briefly introduce yourself, explain how you help incarcerated learners master vocational coursework — you know each learner's pathway, course, lesson and progress — and invite them to ask anything about the platform."
                  style={{ display: 'block', width: '100%', height: '100%' }}
                  data-testid="tutor-modal-embed"
                />
              )}
            </div>
            <p className="mkt-modal-p" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>Allow microphone access when your browser asks. In the product, the counselor is preloaded with each learner's live course context.</p>
          </div>
        </div>
      )}
    </section>
  );
};

export const Devices = () => (
  <section className="devices" data-label="In Action" data-testid="devices-section">
    <div className="dev-row reveal">
      <div className="dev dev-laptop">
        <div className="lap-screen"><img src="/screenshots/screenshot-admin.png" alt="Admin analytics on laptop" loading="lazy" /></div>
        <div className="lap-base" />
      </div>
      <div className="dev dev-tablet">
        <div className="tab-frame"><img src="/screenshots/screenshot-youth.png" alt="Learner home on JPay tablet" loading="lazy" /></div>
      </div>
      <div className="dev dev-phone">
        <div className="ph-frame"><div className="ph-screen"><img src="/screenshots/screenshot-learner.png" alt="Learner onboarding on mobile" loading="lazy" /></div></div>
      </div>
    </div>
    <Link to="/prototype" className="dev-link" data-track="devices-demo" data-testid="devices-demo-link">See it in action →</Link>
  </section>
);
